/* eslint-disable react/react-in-jsx-scope */
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import {
  programQueryKeys,
  removeProgramProjectFromProgramsInfoCache,
  upsertProgramProjectInProgramsInfoCache,
} from "@/dashboard/programQueryConfig";
import { useModal } from "@/dashboard/store/store";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostData from "@/hooks/usePostData";
import { createProjectFromTypeSelection } from "@/modules/project-custom/createProjectFromType";
import { useTypeProjectContext } from "@/programs/context/typeProgramContext";
import { useQueryClient } from "@tanstack/react-query";
import { FileText, FolderKanban, ChevronLeft, Layers3 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_ENDPOINT;

type LibraryItem = {
  scope: "project_type" | "call";
  projectTypeId?: number;
  callId?: number;
  preservedCallId?: number | null;
  programId?: number | null;
  autoProjectTypeId?: number | null;
  name: string;
  sourceDeleted?: boolean;
};

type StepKey = "choice" | "library";

const getLibraryItemKey = (item: LibraryItem) =>
  item.scope === "project_type"
    ? `project_type-${item.projectTypeId ?? 0}`
    : `call-${item.callId ?? item.preservedCallId ?? 0}`;

const isSameLibraryItem = (left: LibraryItem | null, right: LibraryItem) => {
  if (!left) return false;
  return getLibraryItemKey(left) === getLibraryItemKey(right);
};

const ProjectCreationChoiceDialog = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { modals, programId, programName, setOpenModal } = useModal();
  const { fetchData } = useFetchDataV2("");
  const library = useFetchDataV2<any>(`${API_URL}/field-forms/library`);
  const { postData } = usePostData<any>();
  const { typesProject } = useTypeProjectContext();
  const open = !!modals["projectCreationChoice"];
  const [step, setStep] = useState<StepKey>("choice");
  const [selectedForm, setSelectedForm] = useState<LibraryItem | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const fallbackProgramId = (() => {
    const match = location.pathname.match(/^\/dashboard\/program\/(\d+)/);
    if (!match?.[1]) return undefined;
    const parsed = Number(match[1]);
    return Number.isFinite(parsed) ? parsed : undefined;
  })();

  const resolvedProgramId = programId ?? fallbackProgramId;
  const resolvedProgramName = programName;

  const typeForms = useMemo<LibraryItem[]>(
    () =>
      Array.isArray(library.data?.projectTypeForms)
        ? library.data.projectTypeForms
        : [],
    [library.data],
  );

  const callForms = useMemo<LibraryItem[]>(
    () => (Array.isArray(library.data?.callForms) ? library.data.callForms : []),
    [library.data],
  );

  const resolveProjectTypeLabel = (typeId: number) => {
    const allTypes = Array.isArray(typesProject) ? typesProject : [];
    const match = allTypes.find((item: any) => Number(item?.id) === Number(typeId));
    const label = String(match?.name ?? "").trim();
    return label || `Type ${typeId}`;
  };

  const resolveProjectType = (typeId: number) => {
    const allTypes = Array.isArray(typesProject) ? typesProject : [];
    return (
      allTypes.find((item: any) => Number(item?.id) === Number(typeId)) ?? {
        id: typeId,
        image: null,
        name: resolveProjectTypeLabel(typeId),
      }
    );
  };

  const buildOptimisticProject = (typeId: number, name: string, tempId: number) => ({
    id: tempId,
    name,
    status: 1,
    soldStatus: 0,
    type: resolveProjectType(typeId),
    isPersonnalized: true,
    personnalized: true,
    is_personnalized: true,
    __optimistic: true,
  });

  const replaceOptimisticProject = ({
    tempId,
    projectId,
    typeId,
    name,
  }: {
    tempId: number;
    projectId: number;
    typeId: number;
    name: string;
  }) => {
    upsertProgramProjectInProgramsInfoCache(
      queryClient,
      resolvedProgramId,
      {
        ...buildOptimisticProject(typeId, name, projectId),
        __optimistic: false,
      },
      tempId,
    );
  };

  const resetState = () => {
    setStep("choice");
    setSelectedForm(null);
    setIsCreating(false);
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  const handleClose = () => {
    resetState();
    setOpenModal({
      name: "projectCreationChoice",
      value: false,
      programId: resolvedProgramId,
      programName: resolvedProgramName,
    });
  };

  const handleUseVerdeoForm = () => {
    handleClose();
    setOpenModal({
      name: "project",
      value: true,
      programId: resolvedProgramId,
      programName: resolvedProgramName,
    });
  };

  const navigateToCreatedProject = (projectId: number) => {
    if (!Number.isFinite(projectId) || projectId <= 0) return;
    const targetPath = `/dashboard/project/create-custom?programId=${resolvedProgramId}&projectId=${projectId}`;
    if (location.pathname !== targetPath) {
      navigate(targetPath);
    }
  };

  const createDirectProjectFromForm = async (form: LibraryItem) => {
    if (!resolvedProgramId) {
      toast({
        className: "text-red-500",
        title: "Veuillez sélectionner un programme avant de créer un projet.",
      });
      return;
    }

    if (!form.projectTypeId) {
      toast({
        className: "text-red-500",
        title: "Type de projet introuvable pour ce formulaire.",
      });
      return;
    }

    const typeId = Number(form.projectTypeId);
    const tempProjectId = -Date.now();
    setIsCreating(true);
    upsertProgramProjectInProgramsInfoCache(
      queryClient,
      resolvedProgramId,
      buildOptimisticProject(typeId, form.name, tempProjectId),
    );
    setOpenModal({
      name: "projectCreationChoice",
      value: false,
      programId: resolvedProgramId,
      programName: resolvedProgramName,
    });

    try {
      const result = await createProjectFromTypeSelection({
        fetchData,
        postData,
        programId: Number(resolvedProgramId),
        defaultProjectName: form.name,
        typeId,
        draftScope: { projectTypeId: typeId },
        allowCreateWithoutNativeFields: true,
      });

      if (!result.success || !result.projectId) {
        removeProgramProjectFromProgramsInfoCache(
          queryClient,
          resolvedProgramId,
          tempProjectId,
        );
        setOpenModal({
          name: "projectCreationChoice",
          value: true,
          programId: resolvedProgramId,
          programName: resolvedProgramName,
        });
        toast({
          className: "text-red-500",
          title: result.message || "Veuillez réessayer : la création du projet a échoué.",
        });
        return;
      }

      replaceOptimisticProject({
        tempId: tempProjectId,
        projectId: Number(result.projectId),
        typeId,
        name: form.name,
      });
      void queryClient.invalidateQueries({
        exact: false,
        queryKey: [programQueryKeys.programsInfo],
      });
      handleClose();
      navigateToCreatedProject(Number(result.projectId));
    } catch (error) {
      removeProgramProjectFromProgramsInfoCache(
        queryClient,
        resolvedProgramId,
        tempProjectId,
      );
      setOpenModal({
        name: "projectCreationChoice",
        value: true,
        programId: resolvedProgramId,
        programName: resolvedProgramName,
      });
      toast({
        className: "text-red-500",
        title: "Veuillez réessayer : la création du projet a échoué.",
      });
      return;
    } finally {
      setIsCreating(false);
    }
  };

  const createDirectProjectFromCallForm = async (form: LibraryItem) => {
    if (!resolvedProgramId) {
      toast({
        className: "text-red-500",
        title: "Veuillez sélectionner un programme avant de créer un projet.",
      });
      return;
    }

    if (!form.callId && !form.preservedCallId) {
      toast({
        className: "text-red-500",
        title: "Formulaire d'appel introuvable.",
      });
      return;
    }

    const typeId = Number(form.autoProjectTypeId ?? 0);
    if (!Number.isFinite(typeId) || typeId <= 0) {
      toast({
        className: "text-red-500",
        title: "Type de projet introuvable pour ce modèle d'appel.",
      });
      return;
    }

    const projectName = resolveProjectTypeLabel(typeId);
    const tempProjectId = -Date.now();
    setIsCreating(true);
    upsertProgramProjectInProgramsInfoCache(
      queryClient,
      resolvedProgramId,
      buildOptimisticProject(typeId, projectName, tempProjectId),
    );
    setOpenModal({
      name: "projectCreationChoice",
      value: false,
      programId: resolvedProgramId,
      programName: resolvedProgramName,
    });

    try {
      const result = await createProjectFromTypeSelection({
        fetchData,
        postData,
        programId: Number(resolvedProgramId),
        defaultProjectName: projectName,
        typeId,
        draftScope: form.callId
          ? { callId: Number(form.callId), projectTypeId: Number(typeId) }
          : { preservedCallId: Number(form.preservedCallId), projectTypeId: Number(typeId) },
        allowCreateWithoutNativeFields: true,
      });

      if (!result.success || !result.projectId) {
        removeProgramProjectFromProgramsInfoCache(
          queryClient,
          resolvedProgramId,
          tempProjectId,
        );
        setOpenModal({
          name: "projectCreationChoice",
          value: true,
          programId: resolvedProgramId,
          programName: resolvedProgramName,
        });
        toast({
          className: "text-red-500",
          title: result.message || "Veuillez réessayer : la création du projet a échoué.",
        });
        return;
      }

      replaceOptimisticProject({
        tempId: tempProjectId,
        projectId: Number(result.projectId),
        typeId,
        name: projectName,
      });
      void queryClient.invalidateQueries({
        exact: false,
        queryKey: [programQueryKeys.programsInfo],
      });
      handleClose();
      navigateToCreatedProject(Number(result.projectId));
    } catch (error) {
      removeProgramProjectFromProgramsInfoCache(
        queryClient,
        resolvedProgramId,
        tempProjectId,
      );
      setOpenModal({
        name: "projectCreationChoice",
        value: true,
        programId: resolvedProgramId,
        programName: resolvedProgramName,
      });
      toast({
        className: "text-red-500",
        title: "Veuillez réessayer : la création du projet a échoué.",
      });
      return;
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateProject = async () => {
    if (!selectedForm) {
      toast({
        className: "text-red-500",
        title: "Veuillez sélectionner un formulaire.",
      });
      return;
    }

    if (selectedForm.scope === "project_type") {
      await createDirectProjectFromForm(selectedForm);
      return;
    }

    await createDirectProjectFromCallForm(selectedForm);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(value) => !value && handleClose()}>
        <DialogContent className="sm:max-w-[980px]">
          <DialogHeader>
            <DialogTitle>
              {step === "choice" ? "Choix du formulaire" : "Sélection d'un formulaire personnalisé"}
            </DialogTitle>
            <DialogDescription>
              {step === "choice"
                ? "Sélectionnez le mode de création le plus adapté à votre projet."
                : "Sélectionnez un formulaire personnalisé pour poursuivre la création du projet."}
            </DialogDescription>
          </DialogHeader>

          {step === "choice" ? (
            <div className="grid gap-4 md:grid-cols-2">
              <button
                type="button"
                onClick={handleUseVerdeoForm}
                className="group rounded-[24px] border border-slate-200 bg-white p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#1d5214] hover:shadow-[0_18px_35px_rgba(29,82,20,0.12)]"
              >
                <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eef6ec] text-[#1d5214]">
                  <FileText className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-semibold text-[#1d5214]">Formulaire Verdeo</h3>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  Utiliser le formulaire standard de Verdeo, puis poursuivre avec la sélection classique du type de projet.
                </p>
              </button>

              <button
                type="button"
                onClick={() => setStep("library")}
                className="group rounded-[24px] border border-slate-200 bg-white p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#1d5214] hover:shadow-[0_18px_35px_rgba(29,82,20,0.12)]"
              >
                <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eef6ec] text-[#1d5214]">
                  <FolderKanban className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-semibold text-[#1d5214]">Formulaire personnalisé</h3>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  Utiliser un formulaire dynamique existant, basé sur un type de projet ou sur un appel à projets.
                </p>
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setStep("choice");
                    setSelectedForm(null);
                  }}
                  className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-[#1d5214]"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Retour
                </button>
                <div className="inline-flex items-center gap-2 rounded-full border border-[#d3e2cf] bg-[#f7fbf6] px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#1d5214]">
                  <Layers3 className="h-3.5 w-3.5" />
                  Etape 2
                </div>
              </div>

              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                    <Layers3 className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Formulaires par type de projet</h2>
                    <p className="text-sm text-slate-500">
                      {typeForms.length} formulaire{typeForms.length > 1 ? "s" : ""} disponible{typeForms.length > 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {typeForms.map((item) => (
                    <button
                      key={`type-${item.projectTypeId}`}
                      type="button"
                      onClick={() => setSelectedForm(item)}
                      className={`group flex min-h-[132px] flex-col justify-start rounded-2xl border p-4 text-left shadow-sm transition-all ${
                        selectedForm?.scope === "project_type" && selectedForm?.projectTypeId === item.projectTypeId
                          ? "border-[#1d5214] bg-[#f7fbf6] shadow-[0_18px_35px_rgba(29,82,20,0.12)]"
                          : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-[0_18px_35px_rgba(15,118,110,0.12)]"
                      }`}
                    >
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-600">
                        Type de projet
                      </p>
                      <h3 className="mt-3 break-words text-base font-semibold leading-6 text-slate-900">
                        {item.name}
                      </h3>
                    </button>
                  ))}
                </div>
              </section>

              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-lime-100 text-lime-700">
                    <FolderKanban className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Formulaires d'appel à projets</h2>
                    <p className="text-sm text-slate-500">
                      {callForms.length} formulaire{callForms.length > 1 ? "s" : ""} disponible{callForms.length > 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {callForms.map((item) => (
                    <button
                      key={getLibraryItemKey(item)}
                      type="button"
                      onClick={() => setSelectedForm(item)}
                      className={`group flex min-h-[132px] flex-col justify-start rounded-2xl border p-4 text-left shadow-sm transition-all ${
                        isSameLibraryItem(selectedForm, item)
                          ? "border-[#1d5214] bg-[#f7fbf6] shadow-[0_18px_35px_rgba(29,82,20,0.12)]"
                          : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-lime-300 hover:shadow-[0_18px_35px_rgba(132,204,22,0.12)]"
                      }`}
                    >
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-lime-600">
                        {item.sourceDeleted ? "Appel supprimé" : "Appel à projets"}
                      </p>
                      <h3 className="mt-3 break-words text-base font-semibold leading-6 text-slate-900">
                        {item.name}
                      </h3>
                      {item.sourceDeleted ? (
                        <p className="mt-2 text-xs leading-5 text-amber-700">
                          Modèle conservé, déjà utilisé par un ou plusieurs projets.
                        </p>
                      ) : null}
                    </button>
                  ))}
                </div>
              </section>
            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isCreating}>
              Annuler
            </Button>
            {step === "library" && (
              <Button type="button" onClick={handleCreateProject} disabled={!selectedForm || isCreating}>
                {isCreating ? "Création..." : "Créer le projet"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
};

export default ProjectCreationChoiceDialog;
